import { useState } from "react";
import "./EntitySearchResultList.css";
import defaultUser from "../../../stories/assets/defaultuser.jpg";
import {
  AutocompleteSuggestion,
  AutoCompleteTypeEnum,
} from "../../../store/models/autocomplete";
import { getImageURLFromPath } from "../../../hooks/useImageURLFromPath";
import locationIcon from "../../assets/Icon_Location.svg";
import folderIcon from "../../assets/folder-icon.svg";
import soundwaveIcon from "../../assets/Soundwave_Small_1.svg";
import musoIcon from "../../assets/muso-icon-black-background.svg";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faMicrophoneStand,
  faPuzzlePieceSimple,
  faSpeakers,
  faTools,
} from "@fortawesome/pro-solid-svg-icons";
import {
  EquipmentTypeEnum,
  EquipmentTypeReadableNameMap,
} from "../../../store/models/equipment";

const TEST_PREFIX = "entity-search-result-item";

export interface EntitySearchResultListItemProps {
  autocompleteItem: AutocompleteSuggestion;
  onListItemClick: (item: AutocompleteSuggestion) => void;
}

const equipmentTypeIconMap: Partial<
  Record<EquipmentTypeEnum, React.ReactNode>
> = {
  [EquipmentTypeEnum.HARDWARE]: <FontAwesomeIcon size="lg" icon={faTools} />,
  [EquipmentTypeEnum.SOFTWARE]: (
    <FontAwesomeIcon size="lg" icon={faPuzzlePieceSimple} />
  ),
  [EquipmentTypeEnum.MONITORS]: <FontAwesomeIcon size="lg" icon={faSpeakers} />,
  [EquipmentTypeEnum.MICROPHONES]: (
    <FontAwesomeIcon size="lg" icon={faMicrophoneStand} />
  ),
};

export const EntitySearchResultListItem = ({
  autocompleteItem,
  onListItemClick,
}: EntitySearchResultListItemProps) => {
  const [useFallback, setUseFallback] = useState(false);

  const getImageUrl = () => {
    switch (autocompleteItem.type) {
      case AutoCompleteTypeEnum.USER:
        return getImageURLFromPath(false, autocompleteItem?.path ?? "");
      case AutoCompleteTypeEnum.STUDIO:
        return getImageURLFromPath(true, autocompleteItem?.path ?? "");
      default:
        return defaultUser;
    }
  };

  switch (autocompleteItem.type) {
    case AutoCompleteTypeEnum.USER:
      return (
        <div
          onClick={(e) => {
            e.preventDefault();
            onListItemClick(autocompleteItem);
          }}
          className="entity-search-result-list-item"
          data-testid={`${TEST_PREFIX}-container`}
        >
          <img
            src={useFallback ? defaultUser : getImageUrl()}
            width={44}
            height={44}
            onError={() => {
              setUseFallback(true);
            }}
            data-testid={`${TEST_PREFIX}-user-image`}
            alt="user profile"
          />
          <div
            data-testid={`${TEST_PREFIX}-description`}
            className="entity-search-result-list-item-description"
          >
            <span
              className="b2-semi-bold"
              data-testid={`${TEST_PREFIX}-description-title`}
            >
              {autocompleteItem.display_name}
            </span>
            <p className="b3 mx-2" data-testid={`${TEST_PREFIX}-subtitle`}>
              {autocompleteItem.display_name !== autocompleteItem.username
                ? ` Engineer • @${autocompleteItem.username}`
                : " Engineer"}
            </p>
          </div>
        </div>
      );
    case AutoCompleteTypeEnum.STUDIO:
      return (
        <div
          onClick={(e) => {
            e.preventDefault();
            onListItemClick(autocompleteItem);
          }}
          className="entity-search-result-list-item"
          data-testid={`${TEST_PREFIX}-container`}
        >
          <img
            src={useFallback ? defaultUser : getImageUrl()}
            width={44}
            height={44}
            onError={() => {
              setUseFallback(true);
            }}
            data-testid={`${TEST_PREFIX}-studio-image`}
            alt="studio profile"
          />
          <div
            data-testid={`${TEST_PREFIX}-description`}
            className="entity-search-result-list-item-description"
          >
            <span className="b2-semi-bold" data-testid={`${TEST_PREFIX}-title`}>
              {autocompleteItem.display_name}
            </span>
            <p className="b3 mx-2" data-testid={`${TEST_PREFIX}-subtitle`}>
              {autocompleteItem.display_name !== autocompleteItem.username
                ? ` Studio • @${autocompleteItem.username}`
                : " Studio"}
            </p>
          </div>
        </div>
      );
    case AutoCompleteTypeEnum.LOCATION:
      return (
        <div
          onClick={(e) => {
            e.preventDefault();
            onListItemClick(autocompleteItem);
          }}
          className="entity-search-result-list-item"
          data-testid={`${TEST_PREFIX}-container`}
        >
          <div
            className="entity-search-result-icon"
            style={{ backgroundColor: "var(--sky-light-blue)" }}
            data-testid={`${TEST_PREFIX}-icon`}
          >
            <img
              src={locationIcon}
              alt="location"
              data-testid={`${TEST_PREFIX}-location-image`}
            />
          </div>
          <div
            className="entity-search-result-list-item-description"
            data-testid={`${TEST_PREFIX}-description`}
          >
            <span className="b2-semi-bold" data-testid={`${TEST_PREFIX}-title`}>
              {autocompleteItem.label}
            </span>
          </div>
        </div>
      );
    case AutoCompleteTypeEnum.GENRE:
      return (
        <div
          onClick={(e) => {
            e.preventDefault();
            onListItemClick(autocompleteItem);
          }}
          className="entity-search-result-list-item"
          data-testid={`${TEST_PREFIX}-container`}
        >
          <div
            className="entity-search-result-icon"
            style={{ backgroundColor: "var(--timbre-tan)" }}
            data-testid={`${TEST_PREFIX}-icon`}
          >
            <img
              src={folderIcon}
              alt="genre"
              data-testid={`${TEST_PREFIX}-genre-image`}
            />
          </div>
          <div
            className="entity-search-result-list-item-description"
            data-testid={`${TEST_PREFIX}-description`}
          >
            <span className="b2-semi-bold" data-testid={`${TEST_PREFIX}-title`}>
              {autocompleteItem.label}
            </span>
            <p className="b3 mx-2" data-testid={`${TEST_PREFIX}-subtitle`}>
              genre
            </p>
          </div>
        </div>
      );
    case AutoCompleteTypeEnum.SERVICE_TYPE:
      return (
        <div
          onClick={(e) => {
            e.preventDefault();
            onListItemClick(autocompleteItem);
          }}
          className="entity-search-result-list-item"
          data-testid={`${TEST_PREFIX}-container`}
        >
          <div
            className="entity-search-result-icon"
            style={{ backgroundColor: "var(--echo-orange)" }}
            data-testid={`${TEST_PREFIX}-icon`}
          >
            <img
              src={soundwaveIcon}
              alt="service type"
              data-testid={`${TEST_PREFIX}-service-type-image`}
            />
          </div>
          <div
            className="entity-search-result-list-item-description"
            data-testid={`${TEST_PREFIX}-description`}
          >
            <span className="b2-semi-bold" data-testid={`${TEST_PREFIX}-title`}>
              {autocompleteItem.label}
            </span>
            <p className="b3 mx-2" data-testid={`${TEST_PREFIX}-subtitle`}>
              service type
            </p>
          </div>
        </div>
      );
    case AutoCompleteTypeEnum.ARTIST_CREDIT:
      return (
        <div
          onClick={(e) => {
            e.preventDefault();
            onListItemClick(autocompleteItem);
          }}
          className="entity-search-result-list-item"
          data-testid={`${TEST_PREFIX}-container`}
        >
          <div
            className="entity-search-result-icon"
            data-testid={`${TEST_PREFIX}-icon`}
          >
            <img
              src={musoIcon}
              alt="artist credit"
              data-testid={`${TEST_PREFIX}-artist-credit-image`}
            />
          </div>
          <div
            className="entity-search-result-list-item-description"
            data-testid={`${TEST_PREFIX}-description`}
          >
            <p className="b3" data-testid={`${TEST_PREFIX}-title`}>
              has worked with
            </p>
            <span
              className="b2-semi-bold mx-2"
              data-testid={`${TEST_PREFIX}-subtitle`}
            >
              {autocompleteItem.label}
            </span>
          </div>
        </div>
      );
    case AutoCompleteTypeEnum.EQUIPMENT:
      return (
        <div
          onClick={(e) => {
            e.preventDefault();
            onListItemClick(autocompleteItem);
          }}
          className="entity-search-result-list-item"
          data-testid={`${TEST_PREFIX}-container`}
        >
          <div
            className="entity-search-result-icon"
            data-testid={`${TEST_PREFIX}-icon`}
          >
            {
              equipmentTypeIconMap[
                autocompleteItem.equipment_type as EquipmentTypeEnum
              ]
            }
          </div>
          <div
            className="entity-search-result-list-item-description"
            data-testid={`${TEST_PREFIX}-description`}
          >
            <p className="b3" data-testid={`${TEST_PREFIX}-title`}>
              {
                EquipmentTypeReadableNameMap[
                  autocompleteItem.equipment_type as EquipmentTypeEnum
                ]
              }
            </p>
            <span
              className="b2-semi-bold mx-2"
              data-testid={`${TEST_PREFIX}-subtitle`}
            >
              {autocompleteItem.label}
            </span>
          </div>
        </div>
      );
    default:
      return null;
  }
};
